
import * as THREE from "../../../libs/three.js/build/three.module.js";
import {PolygonClipVolume} from "../../utils/PolygonClipVolume.js";
import {ClipTask, ClipMethod} from "../../defines.js";

// Cut (material removed): blue (shallow) -> yellow -> red (deep)
const CUT_RAMP = [
	[0.20, 0.45, 0.90],
	[0.95, 0.85, 0.20],
	[0.85, 0.15, 0.15],
];

// Fill (material added): green (low) -> teal -> blue (high)
const FILL_RAMP = [
	[0.55, 0.85, 0.45],
	[0.15, 0.65, 0.55],
	[0.25, 0.35, 0.85],
];

const CUT_HIGHLIGHT_COLOR = new THREE.Color(0.9, 0.1, 0.1);
const FILL_HIGHLIGHT_COLOR = new THREE.Color(0.1, 0.85, 0.3);

// Builds a Potree polygon clip volume from a region's hull (an array of
// [x, y] points in the point cloud's local coordinate frame), reusing
// Potree's own clip-volume highlighting - the same mechanism the "clip"
// tools in the sidebar use - to tint the *actual points* belonging to that
// region, not just an auxiliary mesh. A polygon clip volume is normally
// drawn by hand by projecting mouse clicks through the current camera; here
// a synthetic top-down orthographic camera is built instead, wide enough to
// frame the region, and the hull's real-world points are projected through
// it exactly the way an interactively-placed marker would be.
function buildRegionClipVolume(pointcloud, hull, centroidZ, color){
	if(!hull || hull.length < 3){
		return null;
	}

	let worldPoints = hull.map(([x, y]) => {
		let p = new THREE.Vector3(x, y, centroidZ);
		pointcloud.localToWorld(p);
		return p;
	});

	let center = new THREE.Vector3();
	for(let p of worldPoints){
		center.add(p);
	}
	center.divideScalar(worldPoints.length);

	let halfWidth = 1;
	let halfHeight = 1;
	for(let p of worldPoints){
		halfWidth = Math.max(halfWidth, Math.abs(p.x - center.x) + 1);
		halfHeight = Math.max(halfHeight, Math.abs(p.y - center.y) + 1);
	}

	let camera = new THREE.OrthographicCamera(-halfWidth, halfWidth, halfHeight, -halfHeight, 0.1, 2000);
	camera.position.set(center.x, center.y, center.z + 500);
	camera.up.set(0, 1, 0);
	camera.lookAt(center.x, center.y, center.z);
	camera.updateMatrixWorld(true);
	camera.updateProjectionMatrix();

	let volume = new PolygonClipVolume(camera);
	volume.highlightColor = color;
	// internt: hører til graverapporten, ikke til Clip-værktøjet (nativeClip.js spejler dem ikke som "Clip Polygon"-rækker)
	volume.pvInternal = true;
	volume.markers = worldPoints.map(p => ({position: p.clone().project(camera)}));
	volume.initialized = true;

	return volume;
}

function rampColor(ramp, t){
	t = Math.max(0, Math.min(1, t));

	let seg = t * (ramp.length - 1);
	let i = Math.min(ramp.length - 2, Math.floor(seg));
	let f = seg - i;
	let a = ramp[i];
	let b = ramp[i + 1];

	return [
		a[0] + (b[0] - a[0]) * f,
		a[1] + (b[1] - a[1]) * f,
		a[2] + (b[2] - a[2]) * f,
	];
}

// Builds a triangulated 3D visualization for one automatically detected
// region (a cut pit or a fill mound): a depth/height-colored "surface" mesh
// (the actual scanned ground), a translucent "reference" mesh (the
// reconstructed surface as if the region had never been disturbed), and
// walls connecting the two along the rim. Every quad is two triangles, so
// this is a proper triangulated surface (a regular-grid TIN), not a flat
// billboard.
class VolumeRegionMesh extends THREE.Object3D{

	constructor(data, ramp){
		super();

		let {grid, max_depth_m} = data;
		let {nx, ny, x0, y0, cell, surface, reference, mask} = grid;

		const at = (arr, r, c) => arr[r * nx + c];
		const isValid = (r, c) => r >= 0 && r < ny && c >= 0 && c < nx && at(mask, r, c) === 1;

		let floorPositions = [];
		let floorColors = [];
		let floorIndices = [];

		let lidPositions = [];
		let lidIndices = [];

		let wallPositions = [];
		let wallColors = [];
		let wallIndices = [];

		const addWall = (xa, ya, xb, yb, zTop, zBottom, color) => {
			let base = wallPositions.length / 3;
			wallPositions.push(
				xa, ya, zTop,
				xb, yb, zTop,
				xb, yb, zBottom,
				xa, ya, zBottom,
			);
			for(let i = 0; i < 4; i++){
				wallColors.push(color[0], color[1], color[2]);
			}
			wallIndices.push(base, base + 1, base + 2,  base, base + 2, base + 3);
		};

		for(let r = 0; r < ny; r++){
			for(let c = 0; c < nx; c++){
				if(!isValid(r, c)){
					continue;
				}

				let zSurface = at(surface, r, c);
				let zReference = at(reference, r, c);
				let depth = Math.abs(zReference - zSurface);
				let color = rampColor(ramp, depth / Math.max(0.01, max_depth_m));

				// walls always span from the higher of the two surfaces to the lower one
				let zTop = Math.max(zSurface, zReference);
				let zBottom = Math.min(zSurface, zReference);

				let x = x0 + c * cell;
				let y = y0 + r * cell;

				{ // "floor": the actual scanned surface, colored by depth/height
					let base = floorPositions.length / 3;
					floorPositions.push(
						x, y, zSurface,
						x + cell, y, zSurface,
						x + cell, y + cell, zSurface,
						x, y + cell, zSurface,
					);
					for(let i = 0; i < 4; i++){
						floorColors.push(color[0], color[1], color[2]);
					}
					floorIndices.push(base, base + 1, base + 2,  base, base + 2, base + 3);
				}

				{ // "lid": the reconstructed, undisturbed reference surface
					let base = lidPositions.length / 3;
					lidPositions.push(
						x, y, zReference,
						x + cell, y, zReference,
						x + cell, y + cell, zReference,
						x, y + cell, zReference,
					);
					lidIndices.push(base, base + 1, base + 2,  base, base + 2, base + 3);
				}

				// walls along the rim, where a neighboring cell falls outside the region
				if(!isValid(r, c - 1)) addWall(x, y, x, y + cell, zTop, zBottom, color);
				if(!isValid(r, c + 1)) addWall(x + cell, y + cell, x + cell, y, zTop, zBottom, color);
				if(!isValid(r - 1, c)) addWall(x + cell, y, x, y, zTop, zBottom, color);
				if(!isValid(r + 1, c)) addWall(x, y + cell, x + cell, y + cell, zTop, zBottom, color);
			}
		}

		if(floorPositions.length > 0){
			let geometry = new THREE.BufferGeometry();
			geometry.setAttribute('position', new THREE.Float32BufferAttribute(floorPositions, 3));
			geometry.setAttribute('color', new THREE.Float32BufferAttribute(floorColors, 3));
			geometry.setIndex(floorIndices);
			geometry.computeVertexNormals();

			let material = new THREE.MeshLambertMaterial({vertexColors: true, side: THREE.DoubleSide});
			let mesh = new THREE.Mesh(geometry, material);
			mesh.name = "surface";
			this.add(mesh);
		}

		if(wallPositions.length > 0){
			let geometry = new THREE.BufferGeometry();
			geometry.setAttribute('position', new THREE.Float32BufferAttribute(wallPositions, 3));
			geometry.setAttribute('color', new THREE.Float32BufferAttribute(wallColors, 3));
			geometry.setIndex(wallIndices);
			geometry.computeVertexNormals();

			let material = new THREE.MeshLambertMaterial({vertexColors: true, side: THREE.DoubleSide});
			let mesh = new THREE.Mesh(geometry, material);
			mesh.name = "walls";
			this.add(mesh);
		}

		if(lidPositions.length > 0){
			let geometry = new THREE.BufferGeometry();
			geometry.setAttribute('position', new THREE.Float32BufferAttribute(lidPositions, 3));
			geometry.setIndex(lidIndices);
			geometry.computeVertexNormals();

			let material = new THREE.MeshBasicMaterial({
				color: 0xffffff,
				transparent: true,
				opacity: 0.15,
				side: THREE.DoubleSide,
				depthWrite: false,
			});
			let mesh = new THREE.Mesh(geometry, material);
			mesh.name = "reference";
			this.add(mesh);
		}
	}
}

// Loads the cut/fill JSON produced by tools/compute_cut_fill_volume.py and
// builds the visualization for both regions (whichever were detected). No
// user digitizing required - the pit/mound is detected automatically.
// Call setEnabled(false) / setEnabled(true) to toggle visibility, and
// dispose() to remove it entirely.
export class CutFillVolume extends THREE.Object3D{

	// options.highlight (default true): fremhæv punkterne i områderne via polygon-clip-volumener og sæt viewerens clip-opgave
	// (den gamle opførsel). false = kun net, labels og kamera - rører hverken viewerens clip-tilstand eller Clip-listen
	// (bruges af den varige graverapport i Registreringer). options.frame (default true): flyv kameraet hen til området.
	constructor(viewer, pointcloud, data, options = {}){
		super();

		this.highlight = options.highlight !== false;
		this.labelsShown = true; // Registreringer-trædets label-knap (setLabelsShown)

		this.name = "cut_fill_volume";
		this.viewer = viewer;
		this.pointcloud = pointcloud;
		this.data = data;
		this.labels = [];
		this.clipVolumes = [];

		// restored when this visualization is disposed, so we don't leave
		// the viewer's global clip state changed behind us
		this.previousClipTask = viewer.clipTask;
		this.previousClipMethod = viewer.clipMethod;

		this._onUpdate = this.updateLabels.bind(this);

		if(data.cut && data.cut.volume_m3 > 0){
			this.addRegion(data.cut, CUT_RAMP, CUT_HIGHLIGHT_COLOR, "Cut");
		}

		if(data.fill && data.fill.volume_m3 > 0){
			this.addRegion(data.fill, FILL_RAMP, FILL_HIGHLIGHT_COLOR, "Fill");
		}

		pointcloud.add(this);

		if(this.labels.length > 0){
			this.viewer.addEventListener("update", this._onUpdate);
		}

		this.applyClipVolumes(true);
		if(options.frame !== false){
			this.frameView();
		}
	}

	addRegion(regionData, ramp, highlightColor, title){
		let mesh = new VolumeRegionMesh(regionData, ramp);
		this.add(mesh);

		let clipVolume = this.highlight ? buildRegionClipVolume(this.pointcloud, regionData.hull, regionData.centroid[2], highlightColor) : null;
		if(clipVolume){
			this.clipVolumes.push(clipVolume);
		}

		let label = document.createElement("div");
		label.setAttribute("data-pv-obstacle", "cutfill"); // labels/LabelManager holder sine labels fri af dette element (ingen visningsregler)
		label.style.position = "absolute";
		label.style.padding = "6px 10px";
		label.style.background = "rgba(0, 0, 0, 0.75)";
		label.style.color = "#fff";
		label.style.font = "13px sans-serif";
		label.style.borderRadius = "4px";
		label.style.pointerEvents = "none";
		label.style.whiteSpace = "nowrap";
		label.style.transform = "translate(-50%, -100%)";
		label.innerHTML = `
			<b>${title} volume:</b> ${regionData.volume_m3.toFixed(1)} m&sup3;<br>
			Area: ${regionData.area_m2.toFixed(1)} m&sup2;<br>
			Max ${title === "Cut" ? "depth" : "height"}: ${regionData.max_depth_m.toFixed(2)} m
		`;

		this.viewer.renderArea.appendChild(label);

		let worldPosition = new THREE.Vector3(...regionData.centroid);
		this.pointcloud.localToWorld(worldPosition);

		this.labels.push({element: label, worldPosition});
	}

	updateLabels(){
		let camera = this.viewer.scene.getActiveCamera();
		let width = this.viewer.renderArea.clientWidth;
		let height = this.viewer.renderArea.clientHeight;

		for(let {element, worldPosition} of this.labels){
			if(!this.visible || !this.labelsShown){
				element.style.display = "none";
				continue;
			}

			let projected = worldPosition.clone().project(camera);

			if(projected.z > 1){
				element.style.display = "none";
				continue;
			}

			let x = (projected.x * 0.5 + 0.5) * width;
			let y = (1 - (projected.y * 0.5 + 0.5)) * height;

			element.style.display = "block";
			element.style.left = `${x}px`;
			element.style.top = `${y}px`;
		}
	}

	// World-space bounding box of the detected region(s), as plain arrays -
	// used by the report tool to crop a screenshot tightly around them.
	getWorldBounds(){
		this.pointcloud.updateMatrixWorld(true);
		let box = new THREE.Box3().setFromObject(this);
		return {min: box.min.toArray(), max: box.max.toArray()};
	}

	// Flies the camera to frame the detected region(s) as tightly as
	// possible, from Potree's own default "isometric-ish" angle (45°
	// yaw, -45° pitch - the same angle a freshly loaded scene starts at)
	// rather than whatever direction the user was last looking from.
	frameView(duration = 600){
		this.pointcloud.updateMatrixWorld(true);

		let box = new THREE.Box3().setFromObject(this);
		if(box.isEmpty()){
			return;
		}

		let center = box.getCenter(new THREE.Vector3());
		let sphere = box.getBoundingSphere(new THREE.Sphere());
		let radius = Math.max(sphere.radius, 0.5);

		let view = this.viewer.scene.view;
		view.yaw = Math.PI / 4;
		view.pitch = -Math.PI / 4;

		let camera = this.viewer.scene.getActiveCamera();
		let fovr = (camera.fov || 60) * Math.PI / 180;
		if(camera.aspect < 1){
			fovr = fovr * camera.aspect;
		}

		let distance = Math.abs(radius / Math.sin(fovr / 2)) * 1.15;
		let position = center.clone().sub(view.direction.clone().multiplyScalar(distance));

		view.setView(position.toArray(), center.toArray(), duration);
	}

	applyClipVolumes(enabled){
		if(this.clipVolumes.length === 0){
			return;
		}

		for(let clipVolume of this.clipVolumes){
			if(enabled){
				this.viewer.scene.addPolygonClipVolume(clipVolume);
			}else{
				this.viewer.scene.removePolygonClipVolume(clipVolume);
			}
		}

		if(enabled){
			this.viewer.setClipTask(ClipTask.HIGHLIGHT);
			this.viewer.setClipMethod(ClipMethod.INSIDE_ANY);
		}
	}

	// Træets label-knap: viser/skjuler volumen-labels (markeringen af punkterne er uændret)
	setLabelsShown(shown){
		this.labelsShown = !!shown;
		this.updateLabels();
	}

	// Træets øje: viser/skjuler markeringen UDEN at flyve kameraet (setEnabled gør det). Markeringen er de polygon-clip-volumener,
	// der fremhæver punkterne i udgravning/påfyldning, så viewerens clip-opgave sættes til Fremhæv, mens den vises, og gendannes,
	// når den skjules.
	setShown(shown){
		shown = !!shown;
		if(this.visible === shown) return;
		this.visible = shown;
		if(this.highlight){
			if(shown){
				this.previousClipTask = this.viewer.clipTask;
				this.previousClipMethod = this.viewer.clipMethod;
				this.applyClipVolumes(true);
			}else{
				this.applyClipVolumes(false);
				this.viewer.setClipTask(this.previousClipTask);
				this.viewer.setClipMethod(this.previousClipMethod);
			}
		}
		this.updateLabels();
	}

	setEnabled(enabled){
		this.visible = enabled;
		this.applyClipVolumes(enabled);

		if(enabled){
			this.frameView();
		}else{
			for(let {element} of this.labels){
				element.style.display = "none";
			}
		}
	}

	dispose(){
		this.viewer.removeEventListener("update", this._onUpdate);

		if(this.highlight && this.visible){
			// kun hvis vi selv ændrede viewerens clip-tilstand (og markeringen ikke allerede er skjult og gendannet) - ellers ville en
			// senere ændring (fx Clip-værktøjet) blive overskrevet
			this.applyClipVolumes(false);
			this.viewer.setClipTask(this.previousClipTask);
			this.viewer.setClipMethod(this.previousClipMethod);
		}

		for(let {element} of this.labels){
			element.remove();
		}
		this.labels = [];

		if(this.parent) this.parent.remove(this);
		this.traverse((object) => {
			if(object.geometry) object.geometry.dispose();
			if(object.material) object.material.dispose();
		});
	}
}

export async function loadCutFillVolume(viewer, pointcloud, url){
	let response = await fetch(url);

	if(!response.ok){
		console.warn(`CutFillVolume: could not load ${url} (${response.status})`);
		return null;
	}

	let data = await response.json();

	return new CutFillVolume(viewer, pointcloud, data);
}
